import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Skeleton } from '@factorialco/deprecated-design-system'
import { F0Box, F0Button, F0Dialog, F0Heading, F0Text } from '@factorialco/f0-react'
import { Tabs } from '@factorialco/f0-react/dist/experimental'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { FeatureFlagAvailabilitiesDocument } from 'generated/resources/graphql'
import { compact } from 'lodash'

import Loading from 'modules/core/components/Loading'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import CompetencyTag from 'modules/job_catalog/components/CompetencyTag'
import EquipmentTag from 'modules/job_catalog/components/EquipmentTag'
import { NodeHeader } from 'modules/job_catalog/components/NodeHeader'
import SalariesCard from 'modules/job_catalog/components/TreeNodePage/SalariesCard'
import WorkingConditionsCard from 'modules/job_catalog/components/TreeNodePage/WorkingConditionsCard'
import CompetenciesAttributes from 'modules/job_catalog/components/TreeNodeAttributes/CompetenciesAttributes'
import OverviewAttributes from 'modules/job_catalog/components/TreeNodeAttributes/OverviewAttributes'
import WorkingConditionsAttributes from 'modules/job_catalog/components/TreeNodeAttributes/WorkingConditionsAttributes'
import { useJobCatalogTreeVisibility } from 'modules/job_catalog/hooks/useJobCatalogTreeVisibility'
import { useTreeNode } from 'modules/job_catalog/hooks/useTreeNode'
import {
  CategoryType,
  DEFAULT_CATEGORIES,
  NODE_TYPES,
  getNavigationForTypeFromFunction,
  getCategoryTranslation,
  getParentChain,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'
import { getCompetenciesForDisplay } from 'modules/job_catalog/lib/competencyUtils'
import { getSalariesGroupedByWorkplace } from 'modules/job_catalog/lib/salaryUtils'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

type SidepanelSectionProps = {
  title: string
  children: ReactNode
  onGoTo?: () => void
}

const SidepanelSection = ({ title, children, onGoTo }: SidepanelSectionProps) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <F0Box
      display="flex"
      flexDirection="column"
      gap="sm"
      paddingY="lg"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <F0Box display="flex" alignItems="center" justifyContent="between">
        <F0Heading content={title} as="h3" />
        {onGoTo && isHovered && (
          <F0Button
            label={String(i18n.t('job_catalog.nodes.actions.go_to'))}
            variant="neutral"
            size="sm"
            onClick={onGoTo}
          />
        )}
      </F0Box>
      <F0Box paddingTop="sm">{children}</F0Box>
    </F0Box>
  )
}

type SidepanelUpsellProps = {
  description: string
  label: string
  moduleRequested: AvailableModules
  featureRequested: Features
  requestReason: ValidReasons
  requestSource: string
  pageSource: string
  showModuleModal?: boolean
}

const SidepanelUpsell = ({
  description,
  label,
  moduleRequested,
  featureRequested,
  requestReason,
  requestSource,
  pageSource,
  showModuleModal,
}: SidepanelUpsellProps) => {
  return (
    <F0Box
      borderRadius="md"
      gap="md"
      padding="md"
      border="default"
      borderColor="secondary"
      display="flex"
      flexDirection="column"
    >
      <F0Text content={description} variant="body" />
      <UpsellingKit.UpsellButton
        moduleRequested={moduleRequested}
        featureRequested={featureRequested}
        requestReason={requestReason}
        requestSource={requestSource}
        pageSource={pageSource}
        label={label}
        size="sm"
        showModuleModal={showModuleModal}
      />
    </F0Box>
  )
}

const SidepanelLoadingSkeleton = () => {
  return (
    <>
      <F0Box paddingTop="sm" paddingBottom="lg" paddingLeft="lg" paddingRight="lg">
        <Box gap="s12">
          <Skeleton width="s200" height="s24" isLoading />
          <Skeleton width="s140" height="s16" isLoading />
          <Box flexDirection="row" gap="s8">
            <Skeleton width="s80" height="s24" isLoading />
            <Skeleton width="s64" height="s24" isLoading />
            <Skeleton width="s64" height="s24" isLoading />
          </Box>
        </Box>
      </F0Box>
      <F0Box paddingY="sm">
        <F0Box borderBottom="default" borderColor="secondary" />
      </F0Box>
      <F0Box paddingLeft="lg" paddingRight="lg" paddingBottom="4xl">
        <Box gap="s24" flexDirection="column">
          <Box gap="s8" flexDirection="column">
            <Skeleton width="s200" height="s20" isLoading />
            <Skeleton width="full" height="s64" isLoading />
          </Box>
          <Box gap="s8" flexDirection="column">
            <Skeleton width="s96" height="s20" isLoading />
            <Skeleton width="full" height="s64" isLoading />
          </Box>
          <Box gap="s8" flexDirection="column">
            <Skeleton width="s140" height="s20" isLoading />
            <Skeleton width="full" height="s120" isLoading />
          </Box>
        </Box>
      </F0Box>
    </>
  )
}

const TreeNodePage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id } = useParams<{ id: string }>()

  const { canSeeCompetencies, canSeeItManagement } = useJobCatalogTreeVisibility()

  const [activeTab, setActiveTab] = useState<CategoryType | null>(null)
  const {
    nodeName,
    nodeType,
    currentNode,
    currentAttributes,
    competencyAttributes,
    salaries,
    childrenInformation,
    workingConditions,
    equipmentDisplayItems,
    loading,
  } = useTreeNode(id)

  const { company } = useCurrentSession()
  const { data: featureFlagsData } = useQuery(FeatureFlagAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      companyId: company.id,
      featureNames: ['dev_job_catalog_tree_sidepanel'],
    },
  })

  const tabs = useMemo(
    () =>
      compact([
        normalizeNodeType(nodeType) !== NODE_TYPES.LEVEL && DEFAULT_CATEGORIES.OVERVIEW,
        canSeeCompetencies && DEFAULT_CATEGORIES.COMPETENCIES,
      ]),
    [nodeType, canSeeCompetencies]
  )

  useEffect(() => {
    const firstTab = tabs[0]
    if (firstTab) setActiveTab(firstTab)
  }, [tabs])

  const workingConditionsTitle =
    getCategoryTranslation(DEFAULT_CATEGORIES.WORKING_CONDITIONS, normalizeNodeType(nodeType)) ?? ''

  const salariesTitle =
    getCategoryTranslation(DEFAULT_CATEGORIES.SALARIES, normalizeNodeType(nodeType)) ?? ''

  const competenciesTitle = getCategoryTranslation(DEFAULT_CATEGORIES.COMPETENCIES) ?? ''
  const itManagementTitle = String(i18n.t('job_catalog.nodes.it_management.one'))

  const groupedSalaries = useMemo(() => getSalariesGroupedByWorkplace(salaries), [salaries])

  const competencyDisplayItems = useMemo(
    () => getCompetenciesForDisplay(competencyAttributes, nodeType),
    [competencyAttributes, nodeType]
  )

  if (!id) return null

  const handleTabChange = (tabId: string) => {
    const newTab = tabs.find((tab) => tab === tabId)
    if (newTab) setActiveTab(newTab)
  }

  const useNewJobCatalogVersion =
    featureFlagsData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'dev_job_catalog_tree_sidepanel'
    )?.enabled ?? false

  const getNodeDetailNavigationParams = () => {
    if (!currentNode) {
      return {
        rootId: '',
        familyId: '',
        functionId: '',
        roleId: '',
        levelId: '',
      }
    }

    const parentChain = getParentChain(currentNode)
    const nodeUuid = currentNode.node_uuid
    const normalizedNodeType = normalizeNodeType(currentNode.type)

    return {
      rootId: parentChain.rootId ?? (normalizedNodeType === NODE_TYPES.ROOT ? nodeUuid : ''),
      familyId: parentChain.familyId ?? (normalizedNodeType === NODE_TYPES.FAMILY ? nodeUuid : ''),
      functionId:
        parentChain.functionId ?? (normalizedNodeType === NODE_TYPES.FUNCTION ? nodeUuid : ''),
      roleId: parentChain.roleId ?? (normalizedNodeType === NODE_TYPES.ROLE ? nodeUuid : ''),
      levelId: normalizedNodeType === NODE_TYPES.LEVEL ? nodeUuid : '',
    }
  }

  const handleGoToSection = (
    section: 'workingConditions' | 'salaries' | 'competencies' | 'itManagement'
  ) => {
    if (!currentNode) return

    const nav = getNavigationForTypeFromFunction(navigation, currentNode.type)
    const navParams = getNodeDetailNavigationParams()

    if (section === 'workingConditions') {
      navigate(nav.workingConditions.fullPath(navParams))
      return
    }

    if (section === 'salaries') {
      navigate(nav.salaries.fullPath(navParams))
      return
    }

    if (section === 'competencies') {
      navigate(nav.competencies.fullPath(navParams))
      return
    }

    navigate(nav.itManagement.fullPath(navParams))
  }

  if (useNewJobCatalogVersion) {
    if (loading) {
      return (
        <F0Dialog
          isOpen
          onClose={() => navigate(navigation.employees.jobCatalog.overview.fullPath())}
          position="right"
          width="md"
          disableContentPadding
        >
          <SidepanelLoadingSkeleton />
        </F0Dialog>
      )
    }

    if (!currentNode) {
      return (
        <F0Dialog
          isOpen
          onClose={() => navigate(navigation.employees.jobCatalog.overview.fullPath())}
          position="right"
          width="md"
          disableContentPadding
        >
          <F0Box paddingTop="sm" paddingBottom="lg" paddingLeft="lg" paddingRight="lg">
            <F0Text content={String(i18n.t('job_catalog.nodes.empty_state.tree.node_not_found'))} />
          </F0Box>
        </F0Dialog>
      )
    }

    return (
      <F0Dialog
        isOpen
        onClose={() => navigate(navigation.employees.jobCatalog.overview.fullPath())}
        position="right"
        width="md"
        disableContentPadding
      >
        <F0Box paddingTop="sm" paddingBottom="lg" paddingLeft="lg" paddingRight="lg">
          <NodeHeader
            name={currentNode.name}
            description={currentNode.description || ''}
            type={currentNode.type}
            childrens={currentNode.children}
            salaries={salaries}
            canEditRole={false}
            currentNode={currentNode}
            isMaximized
          />
        </F0Box>
        <F0Box paddingY="sm">
          <F0Box borderBottom="default" borderColor="secondary" />
        </F0Box>
        <F0Box gap="lg" grow overflow="auto" paddingBottom="4xl" paddingLeft="lg" paddingRight="lg">
          <SidepanelSection
            title={workingConditionsTitle}
            onGoTo={() => handleGoToSection('workingConditions')}
          >
            <WorkingConditionsCard workingConditions={workingConditions} />
          </SidepanelSection>
          <SidepanelSection title={salariesTitle} onGoTo={() => handleGoToSection('salaries')}>
            {groupedSalaries.length > 0 ? (
              <SalariesCard groupedSalaries={groupedSalaries} />
            ) : (
              <F0Text
                content={String(i18n.t('job_catalog.nodes.empty_state.salaries.title'))}
                variant="description"
              />
            )}
          </SidepanelSection>
          <SidepanelSection
            title={competenciesTitle}
            onGoTo={() => handleGoToSection('competencies')}
          >
            {canSeeCompetencies ? (
              competencyDisplayItems.length > 0 ? (
                <F0Box border="default" borderColor="secondary" borderRadius="md">
                  <F0Box padding="md">
                    <F0Box display="flex" flexWrap="wrap" gap="md">
                      {competencyDisplayItems.map((item) => (
                        <CompetencyTag
                          key={item.id}
                          name={item.name}
                          isInherited={item.isInherited}
                          inheritedFromName={item.inheritedFromName}
                        />
                      ))}
                    </F0Box>
                  </F0Box>
                </F0Box>
              ) : (
                <F0Text
                  content={String(i18n.t('job_catalog.nodes.empty_state.competency.title'))}
                  variant="description"
                />
              )
            ) : (
              <SidepanelUpsell
                description={String(
                  i18n.t('job_catalog.nodes.empty_state.competency.upsell.description')
                )}
                label={String(
                  i18n.t('job_catalog.nodes.empty_state.competency.upsell.button_label')
                )}
                moduleRequested={AvailableModules.competencies}
                featureRequested={Features.competencies_core}
                requestReason={ValidReasons.MissingAddon}
                requestSource="job_catalog_competencies_sidepanel"
                pageSource="job_catalog_competencies_sidepanel"
                showModuleModal
              />
            )}
          </SidepanelSection>
          <SidepanelSection
            title={itManagementTitle}
            onGoTo={() => handleGoToSection('itManagement')}
          >
            {canSeeItManagement ? (
              equipmentDisplayItems.length > 0 ? (
                <F0Box border="default" borderColor="secondary" borderRadius="md">
                  <F0Box padding="md">
                    <F0Box display="flex" flexWrap="wrap" gap="md">
                      {equipmentDisplayItems.map((item) => (
                        <EquipmentTag key={item.id} item={item} />
                      ))}
                    </F0Box>
                  </F0Box>
                </F0Box>
              ) : (
                <F0Text
                  content={String(i18n.t('job_catalog.nodes.empty_state.it_management.title'))}
                  variant="description"
                />
              )
            ) : (
              <SidepanelUpsell
                description={String(
                  i18n.t('job_catalog.nodes.empty_state.it_management.upsell.description')
                )}
                label={String(
                  i18n.t('job_catalog.nodes.empty_state.it_management.upsell.button_label')
                )}
                moduleRequested={AvailableModules.it_management}
                featureRequested={Features.it_management_v2_core}
                requestReason={ValidReasons.MissingFeature}
                requestSource="job_catalog_equipment_sidepanel"
                pageSource="job_catalog_equipment_sidepanel"
                showModuleModal
              />
            )}
          </SidepanelSection>
        </F0Box>
      </F0Dialog>
    )
  }

  return (
    <Sidepanel
      onClose={() => navigate(navigation.employees.jobCatalog.overview.fullPath())}
      open
      width="wide"
      title={nodeName ?? ''}
      boxPadding={false}
    >
      {loading ? (
        <Loading expand />
      ) : !currentNode ? null : (
        <>
          <NodeHeader
            name={currentNode.name}
            description={currentNode.description || ''}
            type={currentNode.type}
            childrens={currentNode.children}
            salaries={salaries}
            canEditRole={false}
            currentNode={currentNode}
            isMaximized
          />
          <Box gap="s16" height="full-view-height">
            {tabs.length > 0 && (
              <Tabs
                tabs={tabs.map((value) => ({
                  label: getCategoryTranslation(value, normalizeNodeType(nodeType)) ?? '',
                  id: value,
                }))}
                setActiveTabId={handleTabChange}
                activeTabId={activeTab ?? undefined}
              />
            )}
            <Box height="full">
              {activeTab === DEFAULT_CATEGORIES.COMPETENCIES && (
                <CompetenciesAttributes
                  items={currentAttributes.filter((item) => item.type === activeTab)}
                />
              )}
              {activeTab === DEFAULT_CATEGORIES.WORKING_CONDITIONS && (
                <WorkingConditionsAttributes
                  items={currentAttributes.filter((item) => item.type === activeTab)}
                />
              )}
              {activeTab === DEFAULT_CATEGORIES.OVERVIEW && (
                <OverviewAttributes
                  currentNode={currentNode}
                  childrenInformation={childrenInformation}
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </Sidepanel>
  )
}

export default TreeNodePage
